/**
 * Hook form tạo/sửa bất động sản trong admin.
 *
 * Quản lý state form, chọn địa điểm (tỉnh/xã), upload ảnh, validate và gọi API lưu tin.
 */
import { useEffect, useState } from 'react';
import { categoryApi } from '../../../categories/api/categoryApi';
import { locationApi } from '../../../locations/api/locationApi';
import { propertyApi } from '../../../properties/api/propertyApi';
import { PROPERTY_IMAGE_UPLOAD } from '../../../../shared/constants/files';
import { getApiErrorMessage } from '../../../../shared/utils/apiErrors';
import { UI_MESSAGES } from '../../../../shared/constants/messages';
import {
  ADMIN_PUBLICATION_STATUS_OPTIONS,
  PROPERTY_STATUSES,
  PUBLICATION_STATUSES,
  SUPERADMIN_PUBLICATION_STATUS_OPTIONS,
  TRANSACTION_TYPES,
} from '../../../../shared/constants/property';
import { USER_ROLES } from '../../../../shared/constants/users';

/** Giá trị form rỗng khi tạo tin mới. */
export const emptyPropertyForm = {
  category_id: '',
  location_id: '',
  transaction_type: TRANSACTION_TYPES.sale,
  title: '',
  description: '',
  price: '',
  location: '',
  country: 'Vietnam',
  province: '',
  district: '',
  ward: '',
  area: '',
  frontage: '',
  plot_length: '',
  bedrooms: '',
  master_bedrooms: '',
  bathrooms: '',
  toilets: '',
  kitchens: '',
  living_rooms: '',
  gym_rooms: '',
  worship_rooms: '',
  floors: '',
  has_parking: false,
  has_balcony: false,
  has_elevator: false,
  has_garden: false,
  has_swimming_pool: false,
  has_security: false,
  status: PROPERTY_STATUSES.active,
  publication_status: PUBLICATION_STATUSES.draft,
};

const parsePositiveNumber = (value) => {
  const parsedValue = Number(value);

  return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : null;
};

/** Tính chiều dài mặt hậu từ diện tích và mặt tiền (nếu đủ dữ liệu). */
const calculatePlotLength = (area, frontage) => {
  const areaValue = parsePositiveNumber(area);
  const frontageValue = parsePositiveNumber(frontage);

  if (!areaValue || !frontageValue) {
    return '';
  }

  return String(Number((areaValue / frontageValue).toFixed(2)));
};

const getPublicationStatusOptionsForRole = (userRole) =>
  userRole === USER_ROLES.superAdmin
    ? SUPERADMIN_PUBLICATION_STATUS_OPTIONS
    : ADMIN_PUBLICATION_STATUS_OPTIONS;

/** Map response API sang shape form (boolean tiện ích, id danh mục, ...). */
const normalizePropertyForm = (property) => ({
  category_id: property.category?.id || '',
  location_id: property.location_id || '',
  transaction_type: TRANSACTION_TYPES.sale,
  title: property.title || '',
  description: property.description || '',
  price: property.price || '',
  location: property.location || '',
  country: property.country || 'Vietnam',
  province: property.province || '',
  district: property.district || '',
  ward: property.ward || '',
  area: property.area || '',
  frontage: property.frontage || '',
  plot_length: property.plot_length || '',
  bedrooms: property.bedrooms ?? '',
  master_bedrooms: property.master_bedrooms ?? '',
  bathrooms: property.bathrooms ?? '',
  toilets: property.toilets ?? '',
  kitchens: property.kitchens ?? '',
  living_rooms: property.living_rooms ?? '',
  gym_rooms: property.gym_rooms ?? '',
  worship_rooms: property.worship_rooms ?? '',
  floors: property.floors ?? '',
  has_parking: Boolean(property.features?.parking),
  has_balcony: Boolean(property.features?.balcony),
  has_elevator: Boolean(property.features?.elevator),
  has_garden: Boolean(property.features?.garden),
  has_swimming_pool: Boolean(property.features?.swimming_pool),
  has_security: Boolean(property.features?.security),
  status: property.status || PROPERTY_STATUSES.active,
  publication_status: property.publication_status || PUBLICATION_STATUSES.published,
});

/** Chuẩn bị payload gửi API: null cho field rỗng, ghép chuỗi địa chỉ hiển thị. */
const buildPropertyPayload = (propertyForm, isEditing) => {
  const propertyPayload = {
    ...propertyForm,
    transaction_type: TRANSACTION_TYPES.sale,
    location: [propertyForm.ward, propertyForm.province].filter(Boolean).join(', '),
    address: null,
    furnished_status: null,
    legal_status: null,
    price_per_sqm: null,
  };

  Object.keys(propertyPayload).forEach((fieldName) => {
    if (propertyPayload[fieldName] === '') {
      propertyPayload[fieldName] = null;
    }
  });

  if (!isEditing) {
    delete propertyPayload.status;
  }

  if (propertyPayload.area && propertyPayload.frontage) {
    propertyPayload.plot_length = calculatePlotLength(propertyPayload.area, propertyPayload.frontage);
  }

  return propertyPayload;
};

const buildImageFormData = (selectedImages) => {
  const imageFormData = new FormData();
  selectedImages.forEach((selectedImage) => imageFormData.append('images[]', selectedImage));

  return imageFormData;
};

const validatePropertyForm = (propertyForm, { selectedImages, existingImageCount }) => {
  if (!propertyForm.description?.trim()) {
    return UI_MESSAGES.propertyDescriptionRequired;
  }

  if (Number(propertyForm.price) <= 0) {
    return UI_MESSAGES.propertyPriceRequired;
  }

  if (propertyForm.area && Number(propertyForm.area) <= 0) {
    return UI_MESSAGES.propertyAreaRequired;
  }

  if (!propertyForm.province || !propertyForm.ward || !propertyForm.location_id) {
    return UI_MESSAGES.propertyLocationRequired;
  }

  if (selectedImages.length === 0 && existingImageCount === 0) {
    return UI_MESSAGES.propertyImagesRequired;
  }

  return '';
};

/**
 * Hook form admin BĐS: load tin khi edit, chọn địa điểm, validate và lưu kèm ảnh.
 *
 * @param {string|undefined} propertyId - Id tin khi chỉnh sửa; undefined khi tạo mới.
 * @param {string} [userRole=USER_ROLES.admin] - Quyền user (ảnh hưởng option xuất bản).
 * @returns {Object} propertyForm, handlers, trạng thái loading/submit
 */
export const useAdminPropertyForm = (propertyId, userRole = USER_ROLES.admin) => {
  const isEditingProperty = Boolean(propertyId);
  const [propertyForm, setPropertyForm] = useState(emptyPropertyForm);
  const publicationStatusOptions = getPublicationStatusOptionsForRole(userRole);
  const [categoryList, setCategoryList] = useState([]);
  const [locationTree, setLocationTree] = useState([]);
  const [selectedImages, setSelectedImages] = useState([]);
  const [existingImageCount, setExistingImageCount] = useState(0);
  const [isLoadingProperty, setIsLoadingProperty] = useState(isEditingProperty);
  const [isSubmittingProperty, setIsSubmittingProperty] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    let isMounted = true;

    Promise.all([categoryApi.getCategories(), locationApi.getLocations()]).then(
      ([categoryResponse, locationResponse]) => {
        if (isMounted) {
          setCategoryList(categoryResponse.data.data);
          setLocationTree(locationResponse.data.data);
        }
      }
    );

    return () => {
      isMounted = false;
    };
  }, []);

  // Chế độ sửa: tải chi tiết tin và điền form
  useEffect(() => {
    if (!isEditingProperty) {
      return undefined;
    }

    let isMounted = true;

    propertyApi
      .getAdminProperty(propertyId)
      .then((response) => {
        if (isMounted) {
          const property = response.data.data;
          setPropertyForm(normalizePropertyForm(property));
          setExistingImageCount(property.images?.length || 0);
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoadingProperty(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [propertyId, isEditingProperty]);

  const updatePropertyField = (fieldName, fieldValue) => {
    setPropertyForm((currentPropertyForm) => {
      const nextPropertyForm = {
        ...currentPropertyForm,
        [fieldName]: fieldValue,
      };

      if (fieldName === 'area' || fieldName === 'frontage') {
        nextPropertyForm.plot_length = calculatePlotLength(
          nextPropertyForm.area,
          nextPropertyForm.frontage
        );
      }

      return nextPropertyForm;
    });
  };

  const selectCity = (cityName) => {
    setPropertyForm((currentPropertyForm) => ({
      ...currentPropertyForm,
      province: cityName,
      district: '',
      ward: '',
      location_id: '',
      location: cityName,
    }));
  };

  const selectWard = (wardId) => {
    const selectedCity = locationTree.find((city) => city.name === propertyForm.province);
    const selectedWard = selectedCity?.children?.find(
      (ward) => String(ward.id) === String(wardId)
    );

    if (!selectedWard) {
      setPropertyForm((currentPropertyForm) => ({
        ...currentPropertyForm,
        ward: '',
        location_id: '',
        district: '',
        location: currentPropertyForm.province || '',
      }));
      return;
    }

    setPropertyForm((currentPropertyForm) => ({
      ...currentPropertyForm,
      ward: selectedWard.name,
      location_id: selectedWard.id,
      district: '',
      location: [selectedWard.name, currentPropertyForm.province].filter(Boolean).join(', '),
    }));
  };

  const selectPropertyImages = (images) => {
    const invalidImage = images.find(
      (image) => !PROPERTY_IMAGE_UPLOAD.allowedMimeTypes.includes(image.type)
    );

    if (invalidImage) {
      setSelectedImages([]);
      setErrorMessage(UI_MESSAGES.propertyImageTypeError);
      return;
    }

    setErrorMessage('');
    setSelectedImages(images);
  };

  /** Validate, create/update tin, rồi upload ảnh mới nếu có. Trả về id tin hoặc null khi lỗi. */
  const saveProperty = async () => {
    setIsSubmittingProperty(true);
    setErrorMessage('');

    try {
      const validationMessage = validatePropertyForm(propertyForm, {
        selectedImages,
        existingImageCount,
      });

      if (validationMessage) {
        setErrorMessage(validationMessage);
        return null;
      }

      const propertyPayload = buildPropertyPayload(propertyForm, isEditingProperty);
      const response = isEditingProperty
        ? await propertyApi.updateProperty(propertyId, propertyPayload)
        : await propertyApi.createProperty(propertyPayload);

      const savedPropertyId = isEditingProperty ? propertyId : response.data.data.id;

      if (selectedImages.length) {
        await propertyApi.uploadImages(savedPropertyId, buildImageFormData(selectedImages));
      }

      return savedPropertyId;
    } catch (requestError) {
      setErrorMessage(getApiErrorMessage(requestError, UI_MESSAGES.propertySaveError));
      return null;
    } finally {
      setIsSubmittingProperty(false);
    }
  };

  return {
    propertyForm,
    categoryList,
    locationTree,
    isEditingProperty,
    isLoadingProperty,
    isSubmittingProperty,
    errorMessage,
    selectedImages,
    publicationStatusOptions,
    updatePropertyField,
    selectCity,
    selectWard,
    selectPropertyImages,
    saveProperty,
  };
};
